import { LLMFeedbackResponse } from '@ltvco/refresh-lib/api';
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useLLMFeedback } from '@ltvco/refresh-lib/hooks';
import { useSnackbar } from '@ltvco/refresh-lib/v1';
import { useContext, useEffect, useState } from 'react';

import { CHATBV_GA4_EVENTS } from 'components/ChatBV/constants';
import { Message } from 'components/ChatBV/types';

export const useFeedback = (messages: Message[]) => {
  const [responseIds, setResponseIds] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<Record<string, LLMFeedbackResponse>>(
    {}
  );
  const { trackEventGA4 } = useContext(AppConfig);
  const { enqueueSnackbar } = useSnackbar();
  const { feedbackQueries, createFeedback, updateFeedback } =
    useLLMFeedback(responseIds);

  useEffect(() => {
    setResponseIds(
      messages.map((m) => m.response_id || '').filter((id) => id !== '')
    );
  }, [messages]);

  useEffect(() => {
    if (feedbackQueries.length > 0) {
      feedbackQueries.forEach((q) => {
        if (q.data && (q.data as LLMFeedbackResponse).thumbs_up !== null) {
          const responseData = q.data as LLMFeedbackResponse;
          const responseId = responseData.response_item_id;

          setFeedback((prev) => {
            const existingFeedback = prev[responseId];

            if (
              existingFeedback &&
              existingFeedback.thumbs_up === responseData.thumbs_up
            ) {
              return prev;
            }

            return {
              ...prev,
              [responseId]: responseData,
            };
          });
        }
      });
    }
  }, [feedbackQueries]);

  const handleOpenFeedback =
    (thumbsUp: boolean, response_item_id: string) => () => {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.message.type,
        subtype: thumbsUp
          ? CHATBV_GA4_EVENTS.message.subTypes.thumbs_up_click
          : CHATBV_GA4_EVENTS.message.subTypes.thumbs_down_click,
      });

      const hasRecord =
        typeof feedback[response_item_id]?.thumbs_up === 'boolean';
      const feedbackParams = { response_item_id, thumbs_up: thumbsUp };

      if (hasRecord) {
        updateFeedback.mutate(feedbackParams);
      } else {
        createFeedback.mutate(feedbackParams);
      }

      enqueueSnackbar('Thank you for your feedback!', { variant: 'success' });
    };

  return {
    feedback,
    handleOpenFeedback,
  };
};
